import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import {
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Edit, Delete, Refresh, AccountBalanceWallet } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { useNumberFormat } from '../hooks/useNumberFormat';
import FilteredListLayout from '../components/FilteredListLayout';
import ListActionButton from '../components/ListActionButton';
import { RequirePermission } from '../contexts/PermissionContext';
import {
  deletePlanAdmin,
  fetchPlansAdmin,
} from '../services/subscriptions';
import type { SubscriptionPlan } from '../types';
import usePageTitle from '../hooks/usePageTitle';
import { useListSearchParams } from '../hooks/useListSearchParams';
import LinkPagination from '../components/LinkPagination';
import LinkTableCell from '../components/LinkTableCell';

export default function PlansPage() {
  const { t } = useTranslation(['pages', 'common']);
  usePageTitle(t('pages:plans.title'));
  const { formatCurrency, formatDuration, formatNumber } = useNumberFormat();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const {
    filters,
    page,
    rowsPerPage,
    ordering,
    setParams,
    setPage,
    setRowsPerPage,
    getPageLink,
  } = useListSearchParams({
    filters: ['search'],
    defaultPage: 1,
    defaultPageSize: 10,
    defaultOrdering: 'name',
  });

  const searchValue = filters.search ?? '';
  const orderingParam = ordering || 'name';

  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchPlansAdmin();
      setPlans(data);
    } catch (error) {
      console.error('Error loading plans:', error);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const handleDelete = async (planId: string) => {
    if (!confirm(t('pages:plans.deleteConfirm'))) {
      return;
    }
    try {
      await deletePlanAdmin(planId);
      loadPlans();
    } catch (error) {
      console.error('Error deleting plan:', error);
    }
  };

  const headerActions = (
    <>
      <ListActionButton variant="outlined" startIcon={<Refresh />} onClick={loadPlans}>
        {t('common:actions.refresh')}
      </ListActionButton>
      <RequirePermission permission="subscriptions_plans_create">
        <ListActionButton
          variant="contained"
          startIcon={<Add />}
          component={RouterLink}
          to="/subscriptions/plans/new"
        >
          {t('pages:plans.new')}
        </ListActionButton>
      </RequirePermission>
    </>
  );

  const filteredPlans = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();
    const list = normalizedSearch
      ? plans.filter((plan) =>
          `${plan.name} ${plan.description ?? ''}`.toLowerCase().includes(normalizedSearch)
        )
      : plans;

    return [...list].sort((a, b) => {
      if (orderingParam === '-name') {
        return b.name.localeCompare(a.name);
      }
      return a.name.localeCompare(b.name);
    });
  }, [orderingParam, plans, searchValue]);

  const paginatedPlans = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredPlans.slice(start, start + rowsPerPage);
  }, [filteredPlans, page, rowsPerPage]);

  const filterControls = (
    <TextField
      size="small"
      label={t('common:actions.search')}
      placeholder={t('pages:plans.filters.searchPlaceholder')}
      value={searchValue}
      onChange={(event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setParams({ search: value || undefined }, { resetPage: true });
      }}
      sx={{ minWidth: 240, flex: '1 1 240px' }}
    />
  );

  const table = (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>{t('common:common.name')}</TableCell>
          <TableCell>{t('pages:plans.columns.type')}</TableCell>
          <TableCell>{t('pages:plans.columns.price')}</TableCell>
          <TableCell>{t('pages:plans.columns.interval')}</TableCell>
          <TableCell>{t('pages:plans.columns.minutes')}</TableCell>
          <TableCell>{t('pages:plans.columns.concurrency')}</TableCell>
          <TableCell>{t('pages:plans.columns.publicApi')}</TableCell>
          <TableCell>{t('pages:plans.columns.status')}</TableCell>
          <TableCell align="right">{t('common:actions.actions')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {loading ? (
          <TableRow>
            <TableCell colSpan={9}>
              <Typography color="text.secondary">{t('common:common.loading')}</Typography>
            </TableCell>
          </TableRow>
        ) : filteredPlans.length === 0 ? (
          <TableRow>
            <TableCell colSpan={9}>
              <Typography color="text.secondary">{t('common:common.noData')}</Typography>
            </TableCell>
          </TableRow>
        ) : (
          paginatedPlans.map((plan) => (
            <TableRow key={plan.id} hover>
              <LinkTableCell to={`/subscriptions/plans/${plan.id}`}>
                {plan.name}
              </LinkTableCell>
              <TableCell>
                <Chip
                  size="small"
                  variant="outlined"
                  label={t(`pages:plans.planTypes.${plan.planType || 'ai'}`)}
                />
              </TableCell>
              <TableCell>
                {formatCurrency(Number(plan.priceAmount), plan.priceCurrency)}
              </TableCell>
              <TableCell>{t(`pages:plans.intervals.${plan.billingInterval}`)}</TableCell>
              <TableCell>
                {plan.maxMinutesSeconds === -1
                  ? t('common:subscription.unlimited')
                  : formatDuration(plan.maxMinutesSeconds)}
              </TableCell>
              <TableCell>
                {plan.maxConcurrentCalls === -1
                  ? t('common:subscription.unlimited')
                  : formatNumber(plan.maxConcurrentCalls)}
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  color={plan.enablePublicApi ? 'success' : 'default'}
                  label={plan.enablePublicApi ? t('common:status.enabled') : t('common:status.disabled')}
                />
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  color={plan.isActive ? 'success' : 'default'}
                  label={plan.isActive ? t('common:status.active') : t('common:status.inactive')}
                />
              </TableCell>
              <TableCell align="right">
                <RequirePermission permission="subscriptions_plans_update">
                  <IconButton
                    component={RouterLink}
                    to={`/subscriptions/plans/${plan.id}`}
                    size="small"
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </RequirePermission>
                <RequirePermission permission="subscriptions_plans_delete">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(plan.id)}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </RequirePermission>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );

  return (
    <FilteredListLayout
      headerProps={{
        icon: <AccountBalanceWallet />,
        title: t('pages:plans.title'),
        subtitle: t('pages:plans.subtitle'),
        actions: headerActions,
      }}
      filterControls={filterControls}
      table={table}
      tableFooter={(
        <LinkPagination
          count={filteredPlans.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          onRowsPerPageChange={(event) => setRowsPerPage(Number(event.target.value))}
          getPageLink={getPageLink}
        />
      )}
    />
  );
}
